<script setup lang="ts">
import { computed } from "vue";
import { UiText, UiIcon, UiTooltip, UiProgressBar } from "@shopexperts/shared";
import { useExpertWalletLevelProgress } from "@/app/composables/wallet";

const props = withDefaults(
  defineProps<{
    actingOrgId: number | null | undefined;
    /** Visual density. `mobile` is slightly roomier for touch targets. */
    variant?: "sidebar" | "mobile";
  }>(),
  {
    variant: "sidebar",
  },
);

const { currentLevel, nextLevel, lifetimeGrossCents, remainingGrossCents, currency, isLoading } =
  useExpertWalletLevelProgress({
    actingOrgId: computed(() => props.actingOrgId),
  });

function formatCompactCurrency(cents: number, currencyCode: string | null): string {
  const safeCurrency = (currencyCode || "USD").trim().toUpperCase();
  const value = cents / 100;
  try {
    return value
      .toLocaleString("en-US", {
        style: "currency",
        currency: /^[A-Z]{3}$/.test(safeCurrency) ? safeCurrency : "USD",
        notation: "compact",
        maximumFractionDigits: 1,
      })
      .replace(/K/g, "k")
      .replace(/M/g, "m")
      .replace(/B/g, "b");
  } catch {
    return value.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 1,
    });
  }
}

const currentTitle = computed(() => currentLevel.value?.public_title ?? "Getting started");

const hasNextLevel = computed(() => Boolean(nextLevel.value));

const targetGrossCents = computed(() => {
  if (nextLevel.value) {
    return nextLevel.value.auto_enroll_threshold_gross_cents;
  }
  if (remainingGrossCents.value !== null) {
    return lifetimeGrossCents.value + remainingGrossCents.value;
  }
  return lifetimeGrossCents.value;
});

const percent = computed(() => {
  const target = targetGrossCents.value;
  if (!hasNextLevel.value) return 100;
  if (target <= 0) return 0;
  return Math.min(100, Math.max(0, (lifetimeGrossCents.value / target) * 100));
});

const percentLabel = computed(() => `${Math.round(percent.value)}%`);

const progressCaption = computed(() => {
  if (!hasNextLevel.value) return null;
  const earned = formatCompactCurrency(lifetimeGrossCents.value, currency.value);
  const target = formatCompactCurrency(targetGrossCents.value, currency.value);
  return `${earned} of ${target} toward ${nextLevel.value?.public_title ?? "the next level"}`;
});

const levelTooltip = computed(() => {
  const description = currentLevel.value?.public_description;
  if (description) return description;
  if (hasNextLevel.value) {
    return "Your gross sales determine your expert level. Reach the next threshold to level up and unlock a better commission rate.";
  }
  return "You've reached the highest expert level.";
});
</script>

<template>
  <div :class="variant === 'mobile' ? 'space-y-2' : 'space-y-1.5'">
    <!-- Loading skeleton -->
    <template v-if="isLoading">
      <div class="flex items-center justify-between">
        <div class="h-3 w-20 animate-pulse rounded bg-se-gray-200" />
        <div class="h-3 w-8 animate-pulse rounded bg-se-gray-200" />
      </div>
      <div class="h-2 w-full animate-pulse rounded-full bg-se-gray-200" />
      <div class="h-3 w-32 animate-pulse rounded bg-se-gray-200" />
    </template>

    <template v-else>
      <div class="flex min-w-0 items-center gap-1.5">
        <UiText
          as="span"
          variant="override"
          :text="currentTitle"
          class="truncate font-semibold text-se-dark"
          :class="variant === 'mobile' ? 'text-h3' : 'text-h4'"
        />
        <UiTooltip :content="levelTooltip">
          <UiIcon name="help-circle" class="h-4 w-4 shrink-0 text-se-gray-500" />
        </UiTooltip>
      </div>

      <div class="flex items-center gap-2.5">
        <UiProgressBar
          :value="percent"
          tone="green"
          :size="variant === 'mobile' ? 'md' : 'sm'"
          class="flex-1"
          :aria-label="`Progress toward ${nextLevel?.public_title ?? 'next level'}`"
        />
        <UiText
          v-if="hasNextLevel"
          as="span"
          variant="override"
          :text="percentLabel"
          class="shrink-0 font-bold tabular-nums text-se-dark"
          :class="variant === 'mobile' ? 'text-h3' : 'text-h5'"
        />
      </div>

      <UiText
        as="p"
        variant="override"
        :text="progressCaption ?? 'Highest level reached'"
        class="text-se-dark"
        :class="variant === 'mobile' ? 'text-h5' : 'text-h6'"
      />
    </template>
  </div>
</template>
